/**
 * PUT ACCESS BACK — the only operation on this screen that gives something rather than takes it.
 *
 * Two things it has to say, because an operator who assumes either one will tell a learner
 * something untrue:
 *
 *   1. The learner is sent a fresh invitation. The server states this in `regrant_side_effect`
 *      and that sentence is rendered verbatim: somebody who was removed by mistake ten minutes
 *      ago gets a "you have been enrolled" mail again.
 *   2. A REVOKED CERTIFICATE STAYS REVOKED. Re-granting access does not un-revoke anything —
 *      the `CertificateInvalidation` row survives this operation. This is not an undo.
 *
 * `reason` IS OPTIONAL ON THIS ROUTE, and on this route alone, because nothing is being taken
 * away. The box is still offered, unrequired and ungated, because the reason is still the useful
 * thing in three months.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Banner, Button, Dialog } from '../../shared/ui';
import { regrantAccess, type WireAccessResult, type WireOrderScreen } from '../api/paymentOps';
import { ReasonField } from './ReasonField';
import { GrantPlan } from './GrantPlan';
import { OpsFailureNotice, opsAlreadyHappened, useOperationId } from './CertificateChoices';

export function RegrantAccessDialog({
  /**
   * The whole order payload, so `version` and `regrant_side_effect` come from one read.
   *
   * `version` is optimistic concurrency on `order.updated_at`; a stale one is refused with
   * `stale_order_version` instead of restoring access on a picture of the order that has
   * changed since it was drawn.
   */
  screen,
  onClose,
  onDone,
  onReload,
}: {
  screen: WireOrderScreen;
  onClose: () => void;
  onDone: (result: WireAccessResult) => void;
  /** A fresh read of this order. Optional; pass it wherever a refetch exists. */
  onReload?: () => void;
}) {
  const orderRef = screen.order.order_ref;
  const version = screen.version;
  /** The server's own warning that re-granting sends a fresh invitation. Rendered verbatim. */
  const sideEffect = screen.regrant_side_effect;

  const [reason, setReason] = useState('');
  const operation = useOperationId();

  const regrant = useMutation({
    mutationFn: (operationId: string) =>
      regrantAccess(orderRef, {
        operationId,
        // Sent only when the operator typed something. An empty string is what the seam sends
        // for "no reason given", which this route accepts — it is not a validation failure.
        reason: reason.trim() ? reason : undefined,
        version,
      }),
  });

  const result = regrant.data;
  const alreadyHappened = regrant.isError && opsAlreadyHappened(regrant.error);

  const submit = () => {
    if (operation.id === null) return;
    // The id minted when this dialog opened, reused on every retry, so a second press after a
    // failure is recognised as the same operation instead of a second one.
    regrant.mutate(operation.id);
  };

  return (
    <Dialog
      title="Give this learner their access back?"
      wide
      onClose={onClose}
      footer={
        result ? (
          <Button variant="primary" onClick={() => onDone(result)}>
            Done
          </Button>
        ) : (
          <>
            {/* NOT gated on the reason. There is no `disabled` here beyond the browser check,
                because this route has no reason rule to satisfy. */}
            {!alreadyHappened && (
              <Button
                variant="primary"
                busy={regrant.isPending}
                disabled={operation.id === null}
                onClick={submit}
              >
                Give access back
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </>
        )
      }
    >
      {operation.problem !== null && <Banner tone="danger">{operation.problem}</Banner>}

      {result ? (
        <>
          <Banner tone="success">
            Access has been put back. The academy confirms{' '}
            {result.money_moved === false
              ? 'no money moved — this operation never touches Stripe.'
              : 'that money moved, which this operation is not supposed to do. Check the audit trail and Stripe activity for this order.'}
          </Banner>
          <p className="admin-muted">
            Access is now <strong>{result.access_state || 'not stated by the academy'}</strong>.
          </p>
          {result.note ? <p>{result.note}</p> : null}

          <h4>What was put back</h4>
          {/* THE KEY IS `restored`, NOT `grant_decisions`. Reading the wrong one on this answer
              renders an empty plan and tells the operator nothing was restored. */}
          {result.restored === undefined ? (
            <Banner tone="warning">
              The academy did not list what it restored. The audit trail for this operation records
              it.
            </Banner>
          ) : result.restored.length === 0 ? (
            <p className="admin-muted">
              Nothing needed restoring — this order had no revoked grants left to put back.
            </p>
          ) : (
            <GrantPlan decisions={result.restored} prospective={false} />
          )}

          <Banner tone="warning">
            Any certificate that was revoked is <strong>still revoked</strong>. This did not undo
            that.
          </Banner>
        </>
      ) : (
        <>
          <p>
            This restores what this order paid for. No money moves in either direction: nothing is
            charged, nothing is refunded, and Stripe is not contacted.
          </p>

          {/* The server's sentence, verbatim. It is the one that warns about the invitation mail,
              and it is empty on an order where the server has nothing to warn about — an empty
              banner would imply a warning we were not given. */}
          {sideEffect ? <Banner tone="warning">{sideEffect}</Banner> : null}

          <Banner tone="warning">
            <p>
              <strong>A revoked certificate stays revoked.</strong> Giving access back does not
              un-revoke a certificate — the revocation stands, and its verification link still
              shows it as invalid.
            </p>
            <p>
              So this is not an undo of everything that was done to this order. If the learner
              should have their certificate back as well, that is a separate action.
            </p>
          </Banner>

          <ReasonField
            value={reason}
            onChange={setReason}
            // Optional on this route, so there is no problem to report and nothing to gate. A
            // red message under an unrequired box would tell somebody off for nothing.
            showProblem={false}
            label="Why are you doing this? (optional)"
            disabled={regrant.isPending}
          />
        </>
      )}

      {regrant.isError && <OpsFailureNotice error={regrant.error} onReload={onReload} />}
    </Dialog>
  );
}
